// Pure KPI computation functions for the Executive Cockpit.
//
// All KPIs are computed CLIENT-SIDE from fetched table rows because the Laravel
// admin endpoints provide no server-side aggregation or date filtering. Sums are
// bounded by the fetched page size (see useBusinessData). Counts use the server-
// aggregated `count` field where available.
//
// Every function takes already-fetched rows and returns a KPI value — pure, testable.

import { t } from '../i18n';

import { CrudRecord } from '../crud/types';

export type KpiTone = 'positive' | 'negative' | 'neutral' | 'warning';

export type Kpi = {
  label: string;
  value: string;
  sub?: string;
  tone?: KpiTone;
  hint?: string; // small qualifier like "recent"
};

const todayStr = () => new Date().toISOString().slice(0, 10);
const thisMonthPrefix = () => new Date().toISOString().slice(0, 7); // YYYY-MM

export function num(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function money(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export function money2(value: number): string {
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Sum of a field across rows.
export function sumField(rows: CrudRecord[] | undefined, field: string): number {
  return (rows ?? []).reduce((acc, row) => acc + num(row[field]), 0);
}

// Stock quantity for an item — sums across location stock rows.
export function itemStockQty(item: CrudRecord): number {
  const stock = item.stock;
  if (Array.isArray(stock)) return stock.reduce((s, r) => s + num((r as Record<string, unknown>).quantity), 0);
  if (stock && typeof stock === 'object') return num((stock as Record<string, unknown>).quantity);
  return num((item.location_stock as Record<string, unknown> | undefined)?.quantity) || 0;
}

// --- Individual KPIs ---

export function revenueToday(sales: CrudRecord[] | undefined): Kpi {
  const today = todayStr();
  const todays = (sales ?? []).filter((s) => String(s.date ?? '').slice(0, 10) === today && !s.draft);
  const total = sumField(todays, 'grand_total');
  return {
    label: t('kpi.revenueToday'),
    value: money(total),
    sub: t('kpi.salesToday', { count: todays.length }),
    tone: total > 0 ? 'positive' : 'neutral',
    hint: sales && sales.length >= 200 ? t('kpi.recent') : undefined
  };
}

export function revenueThisMonth(sales: CrudRecord[] | undefined): Kpi {
  const prefix = thisMonthPrefix();
  const months = (sales ?? []).filter((s) => String(s.date ?? '').slice(0, 7) === prefix && !s.draft);
  const total = sumField(months, 'grand_total');
  return {
    label: t('kpi.revenueMonth'),
    value: money(total),
    sub: t('kpi.salesThisMonth', { count: months.length }),
    tone: total > 0 ? 'positive' : 'neutral',
    hint: sales && sales.length >= 200 ? t('kpi.recent') : undefined
  };
}

export function cashPosition(accounts: CrudRecord[] | undefined): Kpi {
  const total = (accounts ?? []).reduce(
    (acc, a) => acc + num((a.journal as Record<string, unknown> | undefined)?.balance ?? a.opening_balance),
    0
  );
  return {
    label: t('kpi.cashPosition'),
    value: money(total),
    sub: t('kpi.accounts', { count: accounts?.length ?? 0 }),
    tone: total >= 0 ? 'neutral' : 'negative'
  };
}

export function inventoryValue(items: CrudRecord[] | undefined): Kpi {
  const total = (items ?? []).reduce((acc, item) => acc + num(item.cost) * itemStockQty(item), 0);
  return {
    label: t('kpi.inventoryValue'),
    value: money(total),
    sub: t('kpi.products', { count: items?.length ?? 0 }),
    hint: items && items.length >= 200 ? t('kpi.recent') : undefined
  };
}

export function receivables(sales: CrudRecord[] | undefined): Kpi {
  // 'paid' is a boolean flag on the sales table (tinyint 0/1), NOT a monetary
  // amount. A sale is outstanding when it's finalized (draft=0) and unpaid
  // (paid=0). Drafts are excluded since they're not posted yet.
  const unpaidSales = (sales ?? []).filter((s) => !s.draft && num(s.paid) === 0);
  const outstanding = unpaidSales.reduce((acc, s) => acc + num(s.grand_total), 0);
  const unpaid = unpaidSales.length;
  return {
    label: t('kpi.receivables'),
    value: money(outstanding),
    sub: t('kpi.unpaidInvoices', { count: unpaid }),
    tone: outstanding > 0 ? 'warning' : 'neutral',
    hint: sales && sales.length >= 200 ? t('kpi.recent') : undefined
  };
}

export function payables(purchases: CrudRecord[] | undefined): Kpi {
  // 'paid' is a boolean flag on the purchases table (tinyint 0/1), NOT a
  // monetary amount. Same logic as receivables.
  const unpaidPurchases = (purchases ?? []).filter((p) => !p.draft && num(p.paid) === 0);
  const outstanding = unpaidPurchases.reduce((acc, p) => acc + num(p.grand_total), 0);
  const unpaid = unpaidPurchases.length;
  return {
    label: t('kpi.payables'),
    value: money(outstanding),
    sub: t('kpi.unpaid', { count: unpaid }),
    tone: outstanding > 0 ? 'warning' : 'neutral',
    hint: purchases && purchases.length >= 200 ? t('kpi.recent') : undefined
  };
}

export function ordersWaiting(ordersCount: number): Kpi {
  return {
    label: t('kpi.ordersWaiting'),
    value: String(ordersCount),
    sub: ordersCount > 0 ? t('kpi.requiresAttention') : t('kpi.nonePending'),
    tone: ordersCount > 0 ? 'warning' : 'neutral'
  };
}

export function openCashRegisters(registerRecords: CrudRecord[] | undefined): Kpi {
  const open = (registerRecords ?? []).filter((record) => !record.closed_at).length;
  return {
    label: t('kpi.openCashRegisters'),
    value: String(open),
    sub: open > 0 ? t('kpi.activeRegisterSessions', { count: open }) : t('kpi.noneOpen'),
    tone: 'neutral',
    hint: registerRecords && registerRecords.length >= 200 ? t('kpi.recent') : undefined
  };
}

export type LowStockItem = { id: string; name: string; code?: string; stock: number; alert: number };

export function lowStockItems(items: CrudRecord[] | undefined): LowStockItem[] {
  const result: LowStockItem[] = [];
  for (const item of items ?? []) {
    if (item.is_stock === false || item.is_stock === 0) continue;
    const stock = itemStockQty(item);
    const alertQty = Array.isArray(item.stock) ? num(item.stock[0]?.alert_quantity ?? item.stock[0]?.min_quantity) : 0;
    if (alertQty > 0 && stock <= alertQty) {
      result.push({ id: String(item.id), name: String(item.name ?? item.code ?? ''), code: item.code ? String(item.code) : undefined, stock, alert: alertQty });
    }
  }
  return result;
}

export function lowStockKpi(items: CrudRecord[] | undefined): Kpi {
  const low = lowStockItems(items);
  return {
    label: t('kpi.lowStock'),
    value: String(low.length),
    sub: low.length ? t('kpi.lowStockCount', { count: low.length }) : t('kpi.allHealthy'),
    tone: low.length > 0 ? 'negative' : 'positive',
    hint: items && items.length >= 200 ? t('kpi.recent') : undefined
  };
}
